import { useState } from "react";
import axios from "axios";
import { Loader2, CheckCircle2, Mail } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Contact() {
  const [form, setForm] = useState({
    name: "",
    contact: "",
    email: "",
    company: "",
    message: "",
  });
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (
      !form.name.trim() ||
      !form.contact.trim() ||
      !form.email.trim() ||
      !form.company.trim() ||
      !form.message.trim()
    ) {
      toast.error("Please fill in all fields.");
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API}/contact`, {
        name: form.name.trim(),
        contact: form.contact.trim(),
        email: form.email.trim(),
        company: form.company.trim(),
        message: form.message.trim(),
      });
      setSubmitted(true);
      toast.success("Message sent. We'll be in touch shortly.");
      setForm({ name: "", contact: "", email: "", company: "", message: "" });
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Something went wrong. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div data-testid="contact-page">
      <section className="max-w-7xl mx-auto px-6 lg:px-10 pt-20 lg:pt-28 pb-12">
        <div className="text-xs uppercase tracking-[0.18em] text-orange-600 font-semibold mb-4 mm-fade-up">
          Contact us
        </div>
        <h1 className="mm-fade-up mm-delay-1 font-heading text-4xl sm:text-5xl lg:text-6xl font-light tracking-tight text-slate-900 max-w-3xl leading-[1.05]">
          Let's start a conversation.
        </h1>
        <p className="mm-fade-up mm-delay-2 mt-6 text-base sm:text-lg text-slate-600 leading-relaxed max-w-2xl">
          Drop us a note and we'll get back to you within one business day.
        </p>
      </section>

      <section className="max-w-7xl mx-auto px-6 lg:px-10 pb-24">
        <div className="grid lg:grid-cols-12 gap-10">
          {/* Contact info side */}
          <aside className="lg:col-span-4 space-y-8">
            <div className="border border-slate-200 rounded-xl p-8 bg-slate-50">
              <Mail size={20} className="text-orange-500 mb-4" />
              <div className="text-xs uppercase tracking-[0.18em] text-slate-500 font-medium mb-2">
                Email
              </div>
              <a
                href="mailto:contact@t3partner.in"
                data-testid="contact-email-link"
                className="font-heading text-xl font-medium text-slate-900 hover:text-orange-600 transition-colors break-all"
              >
                contact@t3partner.in
              </a>
              <p className="text-sm text-slate-600 mt-4 leading-relaxed">
                For new engagements, partnerships, or general questions — we read every message.
              </p>
            </div>

            <div className="border border-slate-200 rounded-xl p-8">
              <div className="text-xs uppercase tracking-[0.18em] text-slate-500 font-medium mb-2">
                Office
              </div>
              <p className="text-sm text-slate-700 leading-relaxed">
                T3 Solutions Pvt Ltd
              </p>
              <p className="text-sm text-slate-700 mt-2 leading-relaxed">
                #43, Govt School Road, Opp Water Tank Road,<br />
                Jakkur 560064
              </p>
              <p className="text-sm text-slate-500 mt-3">
                We work with teams worldwide, on your time zone.
              </p>
            </div>
          </aside>

          {/* Form */}
          <div className="lg:col-span-8">
            {submitted ? (
              <div
                data-testid="contact-success"
                className="border border-slate-200 rounded-xl p-10 bg-white text-center"
              >
                <div className="inline-flex items-center justify-center w-14 h-14 rounded-full bg-emerald-50 text-emerald-600 mb-5">
                  <CheckCircle2 size={28} />
                </div>
                <h2 className="font-heading text-2xl font-medium text-slate-900">
                  Message sent.
                </h2>
                <p className="text-slate-600 mt-3 max-w-md mx-auto leading-relaxed">
                  Thanks for reaching out. A notification has been queued to{" "}
                  <span className="font-medium text-slate-900">contact@t3partner.in</span>{" "}
                  and we'll be in touch shortly.
                </p>
                <Button
                  data-testid="contact-send-another"
                  onClick={() => setSubmitted(false)}
                  className="mt-6 bg-blue-600 hover:bg-blue-700 text-white rounded-md h-11 px-6"
                >
                  Send another message
                </Button>
              </div>
            ) : (
              <form
                onSubmit={handleSubmit}
                data-testid="contact-form"
                className="border border-slate-200 rounded-xl p-6 sm:p-10 bg-white space-y-6"
              >
                <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                  <div>
                    <Label htmlFor="contact-name" className="text-xs uppercase tracking-wider text-slate-600 font-medium">
                      Name
                    </Label>
                    <Input
                      id="contact-name"
                      data-testid="contact-name-input"
                      value={form.name}
                      onChange={(e) => update("name", e.target.value)}
                      required
                      className="mt-2 h-11 focus-visible:ring-2 focus-visible:ring-blue-600 focus-visible:ring-offset-0"
                      placeholder="Your full name"
                    />
                  </div>
                  <div>
                    <Label htmlFor="contact-contact" className="text-xs uppercase tracking-wider text-slate-600 font-medium">
                      Contact number
                    </Label>
                    <Input
                      id="contact-contact"
                      data-testid="contact-contact-input"
                      value={form.contact}
                      onChange={(e) => update("contact", e.target.value)}
                      required
                      className="mt-2 h-11 focus-visible:ring-2 focus-visible:ring-blue-600 focus-visible:ring-offset-0"
                      placeholder="+1 555 000 1234"
                    />
                  </div>
                  <div>
                    <Label htmlFor="contact-email" className="text-xs uppercase tracking-wider text-slate-600 font-medium">
                      Email
                    </Label>
                    <Input
                      id="contact-email"
                      data-testid="contact-email-input"
                      type="email"
                      value={form.email}
                      onChange={(e) => update("email", e.target.value)}
                      required
                      className="mt-2 h-11 focus-visible:ring-2 focus-visible:ring-blue-600 focus-visible:ring-offset-0"
                      placeholder="you@company.com"
                    />
                  </div>
                  <div>
                    <Label htmlFor="contact-company" className="text-xs uppercase tracking-wider text-slate-600 font-medium">
                      Company
                    </Label>
                    <Input
                      id="contact-company"
                      data-testid="contact-company-input"
                      value={form.company}
                      onChange={(e) => update("company", e.target.value)}
                      required
                      className="mt-2 h-11 focus-visible:ring-2 focus-visible:ring-blue-600 focus-visible:ring-offset-0"
                      placeholder="Acme Inc."
                    />
                  </div>
                </div>

                <div>
                  <Label htmlFor="contact-message" className="text-xs uppercase tracking-wider text-slate-600 font-medium">
                    Message
                  </Label>
                  <Textarea
                    id="contact-message"
                    data-testid="contact-message-textarea"
                    value={form.message}
                    onChange={(e) => update("message", e.target.value)}
                    required
                    rows={6}
                    className="mt-2 focus-visible:ring-2 focus-visible:ring-blue-600 focus-visible:ring-offset-0 resize-y"
                    placeholder="Tell us a little about what you're working on…"
                  />
                </div>

                <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 pt-2">
                  <p className="text-xs text-slate-500 max-w-md">
                    Your message will be delivered to{" "}
                    <span className="text-slate-900 font-medium">contact@t3partner.in</span>.
                  </p>
                  <Button
                    type="submit"
                    disabled={submitting}
                    data-testid="contact-submit-btn"
                    className="bg-blue-600 hover:bg-blue-700 text-white rounded-md h-12 px-8 text-sm font-semibold tracking-wide w-full sm:w-auto"
                  >
                    {submitting ? (
                      <>
                        <Loader2 className="mr-2 animate-spin" size={16} /> Sending…
                      </>
                    ) : (
                      "Send message"
                    )}
                  </Button>
                </div>
              </form>
            )}
          </div>
        </div>
      </section>
    </div>
  );
}
